/**
 * Founder quote — a sticky portrait beside a large pull-quote whose words light
 * up one by one as the section scrolls through the viewport (emphasis runs glow
 * in the brand blue). Ported from the `.fq` block + its scroll script.
 *
 * The effect is presentation-only: with JS disabled or reduced-motion on, every
 * word renders in its final lit state (handled in the SCSS).
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import styles from './founder-quote.module.scss';

import type { FounderQuote as FounderQuoteContent } from '../content';

interface Word {
  text: string;
  emphasis: boolean;
}

export default function FounderQuote({ content }: { content: FounderQuoteContent }) {
  const sectionRef = useRef<HTMLElement>(null);
  const [litCount, setLitCount] = useState(0);

  // Flatten the segments into individual words, carrying each segment's emphasis.
  const words = useMemo<Word[]>(
    () =>
      content.segments.flatMap(segment =>
        segment.text
          .split(' ')
          .filter(Boolean)
          .map(text => ({ text, emphasis: Boolean(segment.emphasis) }))
      ),
    [content.segments]
  );

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = section.getBoundingClientRect();
      const viewH = window.innerHeight;
      // Mirror GSAP ScrollTrigger's "start: top center / end: bottom center":
      // animation window opens when the section's top reaches the viewport centre
      // and closes when its bottom does, giving the full section height as scrub range.
      const scrolled = viewH - rect.top;
      const progress = Math.max(0, Math.min(1, (scrolled - viewH * 0.5) / (section.offsetHeight * 0.5)));
      setLitCount(Math.round(progress * words.length));
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [words.length]);

  return (
    <section ref={sectionRef} className={styles.section}>
      <div className={styles.stage}>
        <div className={styles.sticky}>
          <div className={styles.photo}>
            {/* eslint-disable-next-line @next/next/no-img-element -- decorative portrait, no LCP/layout benefit from next/image here */}
            <img src={content.photoSrc} alt={content.photoAlt} />
          </div>
          <div className={styles.info}>
            <div className={styles.tag}>{content.tag}</div>
            <div className={styles.name}>{content.name}</div>
            <div className={styles.role}>
              {content.role.split('\n').map((line, index) => (
                <span key={line}>
                  {line}
                  {index < content.role.split('\n').length - 1 ? <br /> : null}
                </span>
              ))}
            </div>
          </div>
          <span className={styles.icon} aria-hidden='true'>“</span>
        </div>
        <div className={styles.textCol}>
          <p className={styles.quote}>
            {words.map((word, index) => {
              const lit = index < litCount;
              const className = [
                styles.word,
                word.emphasis ? styles.emphasis : '',
                lit ? styles.lit : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <span key={`${word.text}-${index}`} className={className}>
                  {word.text}{' '}
                </span>
              );
            })}
          </p>
        </div>
      </div>
    </section>
  );
}
